/* eslint-disable camelcase */
import React, { useContext, useEffect, useState } from 'react';

import SvgIcon from 'src/react_components/svg-icon';

import { CategoryTab, ClientData, CommentCategory } from '../annotator-types';
import { AnnotatorSidebarContext, AnnotatorSidebarDispatchContext } from './annotator-sidebar-context';

type Props = {
  categories: Map<string, CategoryTab>;
  client: ClientData | null;
  open: boolean;
  selectCategory: (category: string) => void;
};

const ExpandedNavigation = ({
  categories, client, open, selectCategory
}: Props): React.JSX.Element => {
  // Get client info
  const clientInfo = client || {
    alt_text: null, logo: null, general_referral: null
  };
  const { alt_text, logo, general_referral } = clientInfo;
  // Get general referral info
  const generalReferral = general_referral || {
    body: null,
    image: null,
    image_alt_text: null,
    trackable_link: null
  };
  const {
    body, image, image_alt_text, trackable_link
  } = generalReferral;

  // Get active category and dispatch function
  const active = useContext(AnnotatorSidebarContext);
  const dispatch = useContext(AnnotatorSidebarDispatchContext);

  // Set up state
  const [all, setAll] = useState<CategoryTab | null>(null);
  const [other, setOther] = useState<CategoryTab[] | null>(null);

  // Set values for all and other categories
  useEffect((): void => {
    setAll(categories.get('All'));
    // Only set other categories if there are more than one
    if (categories.size > 1) setOther(Array.from(categories.values()).slice(1));
  }, [categories]);

  // Set the active category
  useEffect((): void => {
    selectCategory(CommentCategory[active]);
  }, [active]);

  // Set styles for navigation tabs
  const setStyles = (category: CategoryTab): string => [
    'annotator-navigation-tab d-flex align-items-center m-0',
    category?.icon,
    category?.id === active ? 'active' : ''
  ].filter(Boolean).join(' ');

  // Referral image
  const referralImage = image && (
    <img src={image} alt={image_alt_text} />
  );

  // Referral link content
  const linkContent = referralImage || image_alt_text;

  return (
    <div aria-hidden={!open} className="annotator-expanded-navigation flex-shrink-0">
      <div className="navigation-tabs d-flex flex-column flex-shrink-0">
        {/* All */}
        {all && (
          <button
            type="button"
            aria-describedby="tooltip-all"
            className={setStyles(all)}
            onClick={() => dispatch({ type: 'set', payload: 'All' })}
          >
            {/* Icon */}
            <div className="icon-container d-flex align-items-center justify-content-center">
              <SvgIcon icon={all.icon} className="icon-md m-0" />
            </div>

            {/* Title */}
            <h5 className="m-0">{all.title}</h5>

            {/* Annotation count */}
            <span className="annotation-count">{all.count}</span>
          </button>
        )}

        {/* Section heading */}
        <div className="section-heading">
          <span>Specific comments by section</span>
        </div>

        {/* Other comment categories */}
        {other && other.map((category) => (
          <button
            key={`collapsed-${category.id}`}
            type="button"
            aria-current={category.id === active}
            aria-describedby={`tooltip-${category.id}`}
            className={setStyles(category)}
            onClick={() => dispatch({ type: 'set', payload: category.id })}
          >
            {/* Icon */}
            <div className="icon-container d-flex align-items-center justify-content-center">
              <SvgIcon icon={category.icon} className="icon-md m-0" />
            </div>

            {/* Title */}
            <h5 className="m-0">{category.title}</h5>

            {/* Annotation count */}
            <span className="annotation-count">{category.count}</span>
          </button>
        ))}
      </div>

      {/* General canned referral */}
      {general_referral && (
        <div className="client-info-container flex-grow-1">
          <div className="client-info d-flex flex-column">
            {/* Client logo */}
            {logo && <img src={logo} alt={alt_text} />}

            {/* Referral body */}
            {/* eslint-disable-next-line react/no-danger */}
            {body && <p className="m-0" dangerouslySetInnerHTML={{ __html: body }} />}

            {/* Referral link */}
            {trackable_link && (
              <a href={trackable_link} target="_blank" rel="noreferrer">
                {linkContent}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandedNavigation;
